import React, { useEffect, useState, useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Link } from "react-router-dom";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CContainer,
  CRow,
  CSpinner,
  CAlert,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
} from "@coreui/react";
import { CChartDoughnut, CChartBar } from "@coreui/react-chartjs";
import settingsStore from "../../store/SettingsStore";
import cashShiftsStore from "../../store/CashShiftsStore";
import PeriodFilters from "../UI/PeriodFilters/PeriodFilters";
import ServerSelect from "../UI/PeriodFilters/ServerSelect";
import { fmtInt } from "../../utils/chartHelpers";
import { formatYmd } from "../../utils/dateRange";
import { logReportView } from "../../api/reportLogApi";

const CashAnalyticsPage = observer(() => {
  const [selectedServer, setSelectedServer] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [quickRange, setQuickRange] = useState("week");

  const load = useCallback(
    (from, to) => {
      if (!selectedServer || !from || !to) return;
      cashShiftsStore.fetchShifts(selectedServer, from, to, {
        status: "ANY",
        employeesIncludeDeleted: true,
      });
    },
    [selectedServer],
  );

  useEffect(() => {
    const init = async () => {
      const userId = localStorage.getItem("userId");
      if (userId) await settingsStore.getServers(userId);
      const today = new Date();
      const past = new Date(today);
      past.setDate(today.getDate() - 7);
      setFromDate(formatYmd(past));
      setToDate(formatYmd(today));
      logReportView({
        event: "report_view",
        reportKey: "cash-analytics",
        reportTitle: "Кассовая аналитика",
        userId,
        path: "/cash-analytics",
      });
    };
    init();
  }, []);

  useEffect(() => {
    if (!settingsStore.servers?.length) return;
    if (!selectedServer) setSelectedServer(String(settingsStore.servers[0].id));
  }, [settingsStore.servers, selectedServer]);

  useEffect(() => {
    if (!selectedServer || !fromDate || !toDate) return;
    load(fromDate, toDate);
  }, [selectedServer, fromDate, toDate, load]);

  const a = cashShiftsStore.analytics;
  const paymentTotal = a.salesCash + a.salesCard + a.salesCredit;
  const topCashiers = useMemo(() => a.cashierRanking.slice(0, 10), [a.cashierRanking]);

  return (
    <CContainer style={{ flex: "1", marginTop: "120px" }}>
      <h4 className="mb-3">Кассовая аналитика</h4>
      <ServerSelect
        servers={settingsStore.servers}
        value={selectedServer}
        onChange={(e) => setSelectedServer(e.target.value)}
      />
      <PeriodFilters
        fromDate={fromDate}
        toDate={toDate}
        onFromChange={setFromDate}
        onToChange={setToDate}
        onRefresh={load}
        maxDays={90}
        quickRange={quickRange}
        onQuickRangeChange={setQuickRange}
        disabled={cashShiftsStore.isLoading}
      />
      {cashShiftsStore.errorMessage && (
        <CAlert color="danger" className="mt-3">
          {cashShiftsStore.errorMessage}
        </CAlert>
      )}
      {cashShiftsStore.isLoading && (
        <div className="text-center py-4">
          <CSpinner />
        </div>
      )}
      {!cashShiftsStore.isLoading && (
        <>
          <CRow className="g-3 mt-2 mb-4">
            <CCol sm={6} lg={3}>
              <CCard>
                <CCardBody>
                  <div className="text-body-secondary small">Внесения</div>
                  <div className="fs-4 fw-semibold">{fmtInt(a.payIn)} ₽</div>
                </CCardBody>
              </CCard>
            </CCol>
            <CCol sm={6} lg={3}>
              <CCard>
                <CCardBody>
                  <div className="text-body-secondary small">Изъятия</div>
                  <div className="fs-4 fw-semibold">{fmtInt(a.payOut)} ₽</div>
                </CCardBody>
              </CCard>
            </CCol>
            <CCol sm={6} lg={3}>
              <CCard>
                <CCardBody>
                  <div className="text-body-secondary small">Смен с расхождением</div>
                  <div className="fs-4 fw-semibold">{cashShiftsStore.summary.diffCount}</div>
                </CCardBody>
              </CCard>
            </CCol>
            <CCol sm={6} lg={3}>
              <CCard>
                <CCardBody>
                  <div className="text-body-secondary small">Подозрительных</div>
                  <div className="fs-4 fw-semibold">{cashShiftsStore.summary.warningCount}</div>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
          <CRow className="g-4">
            <CCol lg={6}>
              <CCard>
                <CCardHeader>Оплаты по сменам</CCardHeader>
                <CCardBody>
                  <CChartDoughnut
                    data={{
                      labels: ["Наличные", "Карта", "Кредит"],
                      datasets: [
                        {
                          backgroundColor: ["#41B883", "#36A2EB", "#FFCE56"],
                          data: [a.salesCash, a.salesCard, a.salesCredit],
                        },
                      ],
                    }}
                  />
                  <p className="small text-body-secondary mt-2 mb-0">
                    Итого: {fmtInt(paymentTotal)} ₽
                  </p>
                </CCardBody>
              </CCard>
            </CCol>
            <CCol lg={6}>
              <CCard>
                <CCardHeader>Топ кассиров по заказам</CCardHeader>
                <CCardBody>
                  {topCashiers.length > 0 ? (
                    <CChartBar
                      data={{
                        labels: topCashiers.map((c) => c.name),
                        datasets: [
                          {
                            label: "Заказы",
                            backgroundColor: "#321fdb",
                            data: topCashiers.map((c) => c.total),
                          },
                        ],
                      }}
                      options={{ indexAxis: "y" }}
                    />
                  ) : (
                    <p className="text-body-secondary mb-0">Нет данных</p>
                  )}
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
          <CCard className="mt-4">
            <CCardHeader className="d-flex justify-content-between">
              <span>Смены с расхождением</span>
              <Link to="/cash-shifts" className="btn btn-sm btn-outline-primary">
                Все смены
              </Link>
            </CCardHeader>
            <CCardBody>
              <CTable small hover responsive>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>Смена</CTableHeaderCell>
                    <CTableHeaderCell>Кассир</CTableHeaderCell>
                    <CTableHeaderCell>Расхождение</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {a.diffs.slice(0, 20).map((d) => (
                    <CTableRow key={d.shift.id ?? d.shift.sessionNumber}>
                      <CTableDataCell>№{d.shift.sessionNumber}</CTableDataCell>
                      <CTableDataCell>
                        {d.shift.responsibleUserName || d.shift.managerName || "—"}
                      </CTableDataCell>
                      <CTableDataCell>{fmtInt(d.cashDiff)} ₽</CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </CCardBody>
          </CCard>
        </>
      )}
    </CContainer>
  );
});

export default CashAnalyticsPage;
